import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from './firebase';
import { collection, addDoc, getDocs, query, orderBy, limit, where } from 'firebase/firestore';

// Import all images
import logo from './assets/images/Baby Daddy Logo.jpg';
import alden08 from './assets/images/Alden-08.jpg';
import alden10 from './assets/images/Alden-10.jpg';
import alden12 from './assets/images/Alden-12.png';
import alden14 from './assets/images/Alden-14.jpg';
import alden27 from './assets/images/Alden-27.jpg';
import alden37 from './assets/images/Alden-37.jpg';
import alden44 from './assets/images/Alden-44.jpg';
import alden45 from './assets/images/Alden-45.jpg';
import alden46 from './assets/images/Alden-46.jpg';
import alden47 from './assets/images/Alden-47.jpg';
import alden57 from './assets/images/Alden-57.jpg';
import alden58 from './assets/images/Alden-58.jpg';
import puck from './assets/images/Internal Rotating Puck.png';
import clamp from './assets/images/Clamp.png';
import alden18Clear from './assets/images/Alden-18.jpg';
import alden18Blue from './assets/images/Alden-18_Blue.jpg';
import alden18Pink from './assets/images/Alden-18_Pink.jpg';
import alden01 from './assets/images/Alden-01.jpg';
import alden03 from './assets/images/Alden-03.jpg';
import alden16 from './assets/images/Alden-16 (1).jpg';
import founderPhoto from './assets/images/unnamed.jpg';
import cribImage from './assets/images/CRIB.jpeg';
import strollerImage from './assets/images/STROLLER.jpeg';
import contentImage from './assets/images/content.png';
import capsAndCylinder from './assets/images/CapsandCylinder.jpg';

// ============================================
// MOBILE DETECTOR HOOK
// ============================================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// ============================================
// INFO MODAL - Learn More Popup
// ============================================
const InfoModal = ({ isOpen, onClose }) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      {/* Content Box */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 20px 60px rgba(22, 60, 72, 0.15)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '44px',
            height: '44px',
            background: '#F0F4F5',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <h2 style={{ color: '#163C48', fontSize: '1.5rem', fontWeight: 600, marginBottom: '24px', paddingRight: '40px' }}>How It Works</h2>

        {/* Hovering Section */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58737D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Zero Weight on Baby
          </h3>
          <p style={{ color: '#163C48', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '12px' }}>
            The bottle holder hovers gently above the baby, your baby feels no weight from it at all.
          </p>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '12px' }}>
            The holder stays suspended by a reinforced gooseneck attached to a secure clamp. When the clamp is fastened properly to a stable surface and the gooseneck is fully threaded into the clamp, the holder remains stable and elevated, never resting on the baby.
          </p>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, fontStyle: 'italic' }}>
            In our testing with dozens of parents, the holder remained stable and performed as intended when installed correctly.
          </p>
        </div>

        {/* Bottle Guidance Section */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58737D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
              <path d="M10 2v4h4V2" />
            </svg>
            Bottle Guidance
          </h3>
        <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '16px' }}>
          Some milk may drip from a bottle when inverted, this is completely normal, but the bottle should not free-flow on its own. Occasional dripping is fine; continuous streaming is not. For use with this holder, the bottle must require the baby to suck for milk to release.
        </p>
        <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
          Most modern bottles are designed this way, they limit leaking and release milk primarily when a baby sucks, but always check your specific bottle to confirm.
        </p>

        <h4 style={{ color: '#163C48', fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>Examples of bottles designed to limit free-flowing milk:</h4>
        <p style={{ color: '#58737D', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '12px' }}>
          These brands commonly use suction-activated or anti-leak nipple designs (product lines vary):
        </p>
        <ul style={{ color: '#58737D', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: '16px', paddingLeft: '20px', columns: 2, columnGap: '20px' }}>
          <li>Dr. Brown's (Options+)</li>
          <li>Tommee Tippee</li>
          <li>Munchkin Latch</li>
          <li>Comotomo</li>
          <li>Philips Avent Anti-Colic</li>
          <li>Evenflo Balance+</li>
          <li>MAM</li>
          <li>Nanobebe</li>
          <li>Many store-brand anti-colic bottles</li>
        </ul>
        <p style={{ color: '#58737D', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '16px', fontStyle: 'italic' }}>
          "Suction-activated" or "anti-leak" may not appear on the label, but the function is common: the baby must suck to draw milk.
        </p>
        </div>

        {/* Safety Check Section */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58737D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            Quick Bottle Safety Check
          </h3>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '12px' }}>
            Before using any bottle with the holder:
          </p>
          <ol style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '16px', paddingLeft: '20px' }}>
            <li style={{ marginBottom: '6px' }}>Fill the bottle with water or milk.</li>
            <li style={{ marginBottom: '6px' }}>Turn it upside down.</li>
            <li style={{ marginBottom: '6px' }}>Observe the nipple.</li>
          </ol>
          <ul style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '16px', paddingLeft: '20px', listStyle: 'none' }}>
            <li style={{ marginBottom: '6px' }}>If it free-flows, <strong style={{ color: '#ef4444' }}>do not use</strong> that bottle with this product.</li>
            <li>If it only releases milk when suction is applied, it is appropriate for normal supervised bottle-feeding and for this holder.</li>
          </ul>
          <p style={{ color: '#58737D', fontSize: '0.9rem', lineHeight: 1.6, fontStyle: 'italic' }}>
            This simple test ensures you are using the correct type of bottle.
          </p>
        </div>
      </div>

      {/* Inline keyframes for fade animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// SAFETY MODAL - Age Guidelines & Safety
// ============================================
const SafetyModal = ({ isOpen, onClose }) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(22, 60, 72, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      {/* Content Box */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 20px 60px rgba(22, 60, 72, 0.15)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '44px',
            height: '44px',
            background: '#F0F4F5',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <h2 style={{ color: '#163C48', fontSize: '1.5rem', fontWeight: 600, marginBottom: '24px', paddingRight: '40px' }}>Age Guidelines & Safety</h2>

        {/* Supervision Section */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58737D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Supervision
          </h3>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '12px' }}>
            This product is designed to assist parents, not replace them. <strong style={{ color: '#163C48' }}>Always supervise your baby while using the Baby Daddy Holder.</strong>
          </p>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7 }}>
            The holder is intended as an extra hand while a parent is nearby and attentive. Never leave your baby unattended during feeding.
          </p>
        </div>

        {/* Age Guidance Section */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58737D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Recommended Age Range
          </h3>
          <div style={{ background: '#F0F4F5', border: '1px solid #CFD8DC', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ color: '#163C48', fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', textAlign: 'center' }}>2 - 8 Months</p>
            <p style={{ color: '#58737D', fontSize: '0.9rem', textAlign: 'center' }}>Optimal usage window</p>
          </div>
          <ul style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.8, paddingLeft: '20px', marginBottom: '12px' }}>
            <li style={{ marginBottom: '8px' }}><strong style={{ color: '#163C48' }}>Before 2 months:</strong> Babies are still developing feeding coordination. We recommend traditional hand-feeding during this period.</li>
            <li style={{ marginBottom: '8px' }}><strong style={{ color: '#163C48' }}>2-8 months:</strong> Ideal window. Baby has developed good sucking reflexes and can feed comfortably while lying in a safe position.</li>
            <li><strong style={{ color: '#163C48' }}>After 8 months:</strong> Many babies begin sitting up and may prefer holding their own bottle. The holder can still be useful but usage naturally decreases.</li>
          </ul>
        </div>

        {/* Safety Tips Section */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#58737D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Safety Tips
          </h3>
          <ul style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.8, paddingLeft: '20px' }}>
            <li style={{ marginBottom: '8px' }}>Ensure the clamp is securely fastened before each use</li>
            <li style={{ marginBottom: '8px' }}>Check that the gooseneck is fully threaded into the clamp</li>
            <li style={{ marginBottom: '8px' }}>Always stay nearby and attentive while using the holder</li>
            <li style={{ marginBottom: '8px' }}>Use bottles with suction-activated nipples (see "Bottle Safety & Guidelines" on the photo above)</li>
            <li>Clean all components regularly according to care instructions</li>
          </ul>
        </div>

        {/* Disclaimer */}
        <div style={{ background: '#F0F4F5', borderRadius: '12px', padding: '20px', borderLeft: '4px solid #2D6E81' }}>
          <p style={{ color: '#58737D', fontSize: '0.85rem', lineHeight: 1.6, fontStyle: 'italic', marginBottom: '8px' }}>
            Every baby develops differently. These guidelines are general recommendations based on typical developmental milestones.
          </p>
          <p style={{ color: '#58737D', fontSize: '0.85rem', lineHeight: 1.6, fontStyle: 'italic' }}>
            <strong style={{ color: '#163C48' }}>Always consult with your pediatrician</strong> before using any feeding assistance device to ensure it's appropriate for your baby's specific needs and development.
          </p>
        </div>
      </div>

      {/* Inline keyframes for fade animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// PROMISE MODAL - Safety & Certification Roadmap
// ============================================
const PromiseModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(22, 60, 72, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      {/* Content Box */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '650px',
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 20px 60px rgba(22, 60, 72, 0.15)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '44px',
            height: '44px',
            background: '#F0F4F5',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <h2 style={{ color: '#163C48', fontSize: '1.6rem', fontWeight: 700, marginBottom: '28px', paddingRight: '40px' }}>Our Commitment to You.</h2>

        {/* Section 1: CPSC Certification */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2D6E81" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            CPSC Certified Before We Ship.
          </h3>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '12px' }}>
            We refuse to cut corners. Before a single unit leaves our warehouse, the Baby Daddy Holder will be rigorously tested and certified by a CPSC-accredited laboratory.
          </p>
          <div style={{ background: '#F0F4F5', borderRadius: '10px', padding: '16px', borderLeft: '3px solid #2D6E81' }}>
            <p style={{ color: '#163C48', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
              <strong>What this means:</strong> We pass strict federal tests for lead, phthalates, physical stability, and choking hazards. If it doesn't pass US Government standards, it doesn't ship. Period.
            </p>
          </div>
        </div>

        {/* Section 2: Pediatrician Approval */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2D6E81" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
            Medically Reviewed.
          </h3>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7 }}>
            We are currently assembling a board of pediatricians and occupational therapists to review our usage guidelines. When we launch, their names and endorsements will be publicly listed on this website.
          </p>
        </div>

        {/* Section 3: Why the Waitlist */}
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2D6E81" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Why we need you now.
          </h3>
          <p style={{ color: '#58737D', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '12px' }}>
            Going to market the <em>right</em> way, with proper tooling, non-toxic materials, and federal certification, is expensive. We are not a massive corporation; we are parents.
          </p>
          <div style={{ background: '#F0F4F5', borderRadius: '10px', padding: '16px', borderLeft: '3px solid #2D6E81' }}>
            <p style={{ color: '#163C48', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
              Your sign-up is not just a reservation. It is the proof of demand we need to fund these rigorous safety certifications. <strong>You are helping us build this safely.</strong>
            </p>
          </div>
        </div>

        {/* Section 4: What's in the Box */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#163C48', fontSize: '1.15rem', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2D6E81" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
            What comes in the box?
          </h3>
          <div style={{ background: '#F0F4F5', borderRadius: '12px', padding: '20px' }}>
            <ul style={{ color: '#163C48', fontSize: '0.95rem', lineHeight: 2, paddingLeft: '20px', margin: 0 }}>
              <li>The Universal Holder (BPA-Free PETG)</li>
              <li>Inner Rotating Puck</li>
              <li>4 Locking Lids</li>
              <li>Clamp</li>
              <li>Flexible Gooseneck. <em style={{ fontStyle: 'italic', color: '#58737D' }}>The gooseneck has male threads on both sides, so there is no need for any screws.</em></li>
              <li>4 pieces of adhesive non-slip rubber tape for Clamp <em style={{ fontStyle: 'italic', color: '#58737D' }}>(you get two extra pieces)</em></li>
              <li>Detailed Safety, Set Up Instructions, & Supervision Guide</li>
            </ul>
          </div>
        </div>

      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// WAITLIST MODAL - Multi-Step Application
// ============================================
const WaitlistModal = ({ isOpen, onClose }) => {
  const isMobile = useIsMobile();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [priceWouldPay, setPriceWouldPay] = useState('');
  const [priceWouldNotPay, setPriceWouldNotPay] = useState('');
  const [priceMaybePay, setPriceMaybePay] = useState('');
  const [colorPreference, setColorPreference] = useState('');
  const [incomeRange, setIncomeRange] = useState('');
  const [role, setRole] = useState('');
  const [waitlistNumber, setWaitlistNumber] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [surveyErrors, setSurveyErrors] = useState({});

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setEmail('');
        setPriceWouldPay('');
        setPriceWouldNotPay('');
        setPriceMaybePay('');
        setColorPreference('');
        setIncomeRange('');
        setRole('');
        setWaitlistNumber(null);
        setEmailError('');
        setSurveyErrors({});
      }, 300);
    }
  }, [isOpen]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailSubmit = () => {
    if (!email) {
      setEmailError('Please enter your email');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email');
      return;
    }
    setEmailError('');
    setStep(2);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSurveySubmit = async () => {
    // Validate required fields (income is optional)
    const errors = {};
    if (!priceWouldPay) errors.priceWouldPay = true;
    if (!priceWouldNotPay) errors.priceWouldNotPay = true;
    if (!colorPreference) errors.colorPreference = true;
    if (!role) errors.role = true;

    if (Object.keys(errors).length > 0) {
      setSurveyErrors(errors);
      return;
    }

    setSurveyErrors({});
    setIsSubmitting(true);

    try {
      const waitlistRef = collection(db, 'waitlist');

      // Check if email already exists
      const emailQuery = query(waitlistRef, where('email', '==', email));
      const emailSnapshot = await getDocs(emailQuery);

      if (!emailSnapshot.empty) {
        alert('This email has already been used to join the waitlist.');
        setIsSubmitting(false);
        return;
      }

      // Get the current count of waitlist entries to generate sequential number
      const countQuery = query(waitlistRef, orderBy('waitlist_number', 'desc'), limit(1));
      const snapshot = await getDocs(countQuery);

      let number;
      if (snapshot.empty) {
        number = 1; // First signup
      } else {
        number = snapshot.docs[0].data().waitlist_number + 1;
      }

      // Save to Firestore
      const formData = {
        email: email,
        price_would_pay: priceWouldPay,
        price_would_not_pay: priceWouldNotPay,
        color_preference: colorPreference,
        income_range: incomeRange,
        role: role,
        timestamp: new Date().toISOString(),
        waitlist_number: number,
        created_at: new Date()
      };

      await addDoc(collection(db, 'waitlist'), formData);
      console.log('Waitlist signup saved to Firestore:', formData);

      setWaitlistNumber(number);
      localStorage.setItem('waitlistNumber', number.toString());

      // Show success
      setStep(3);

    } catch (error) {
      console.error('Error saving to Firestore:', error);
      alert('There was an error joining the waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const priceOptions = ['$49.99', '$59.99', '$69.99', '$79.99', '$89.99', '$99.99'];
  const priceOptionsNoLowest = ['$59.99', '$69.99', '$79.99', '$89.99', '$99.99'];
  const incomeOptions = ['Under $50k', '$50k - $100k', '$100k - $150k', '$150k - $250k', '$250k+'];
  const roleOptions = ["I'm a Mom", "I'm a Dad", 'Expecting', 'Gift / Other'];

  // Safety Orange color for selected state
  const safetyOrange = '#ff6b35';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(22, 60, 72, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      {/* Modal Content */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: isMobile ? '16px' : '24px',
          padding: isMobile ? (step === 3 ? '40px 20px' : '24px 16px') : (step === 3 ? '60px 40px' : '40px'),
          width: '100%',
          maxWidth: isMobile ? '100%' : (step === 2 ? '600px' : '480px'),
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 25px 50px -12px rgba(22, 60, 72, 0.25)',
          animation: 'scaleIn 0.3s ease',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '36px',
            height: '36px',
            background: '#F0F4F5',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#E0E6E8'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#F0F4F5'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Progress Bar (only show on steps 1-2) */}
        {step < 3 && (
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
              {[1, 2].map((s) => (
                <div
                  key={s}
                  style={{
                    width: '60px',
                    height: '3px',
                    borderRadius: '2px',
                    background: s <= step ? safetyOrange : '#CFD8DC',
                    transition: 'background 0.3s ease',
                  }}
                />
              ))}
            </div>
            <p style={{ textAlign: 'center', fontSize: '12px', color: '#58737D' }}>
              Step {step} of 2
            </p>
          </div>
        )}

        {/* SCREEN 1: THE HOOK */}
        {step === 1 && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#163C48', marginBottom: '12px' }}>
              Secure Your Spot. No payment method required.
            </h2>
            <p style={{ fontSize: '1rem', color: '#58737D', marginBottom: '32px', lineHeight: 1.6 }}>
              The first 1,000 people who sign up get <span style={{ color: safetyOrange, fontWeight: 600 }}>50% OFF</span> the final launch price.
            </p>

            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
              placeholder="Enter your email"
              style={{
                width: '100%',
                padding: '16px 20px',
                fontSize: '16px',
                color: '#163C48',
                background: '#F0F4F5',
                border: emailError ? '1px solid #ef4444' : '1px solid #CFD8DC',
                borderRadius: '12px',
                outline: 'none',
                marginBottom: emailError ? '8px' : '20px',
                transition: 'border 0.2s ease',
              }}
              onFocus={(e) => e.target.style.border = `1px solid #2D6E81`}
              onBlur={(e) => e.target.style.border = emailError ? '1px solid #ef4444' : '1px solid #CFD8DC'}
            />
            {emailError && (
              <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '16px', textAlign: 'left' }}>{emailError}</p>
            )}

            <button
              onClick={handleEmailSubmit}
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                background: '#2D6E81',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 8px 20px rgba(45, 110, 129, 0.25)',
              }}
              onMouseEnter={(e) => { e.target.style.transform = 'scale(1.02)'; e.target.style.boxShadow = '0 10px 30px rgba(45, 110, 129, 0.3)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 8px 20px rgba(45, 110, 129, 0.25)'; }}
            >
              Continue
            </button>
          </div>
        )}

        {/* SCREEN 2: THE CALIBRATION (SURVEY) */}
        {step === 2 && (
          <div>
            <p style={{ fontSize: '0.9rem', color: '#58737D', lineHeight: 1.7, marginBottom: '28px', textAlign: 'center' }}>
              We are finalizing our manufacturing. We cannot lower our quality standards for this product, but we want to make the Baby Daddy Holder accessible to all parents. We cannot sell below $50 when we account for the various production costs that we will be dealing with, but the starting price will not exceed $100. Your honest feedback helps dictate our launch price. Please give us your honest feedback. We need this data for our investors and for our manufacturing partners.
            </p>

            {/* Question 1: How much would you pay? */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 500, color: surveyErrors.priceWouldPay ? '#ef4444' : '#163C48', marginBottom: '12px' }}>
                Based on what you know about the Baby Daddy Holder and what you have seen, how much would you pay for this?
                {surveyErrors.priceWouldPay && <span style={{ color: '#ef4444', marginLeft: '8px' }}>*</span>}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '8px' : '10px' }}>
                {priceOptions.map((price) => (
                  <button
                    key={price}
                    onClick={() => setPriceWouldPay(price)}
                    style={{
                      padding: isMobile ? '12px 8px' : '14px 12px',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      fontWeight: 500,
                      color: priceWouldPay === price ? '#fff' : '#163C48',
                      background: priceWouldPay === price ? safetyOrange : '#F0F4F5',
                      border: priceWouldPay === price ? 'none' : '1px solid #CFD8DC',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (priceWouldPay !== price) {
                        e.target.style.background = '#E0E6E8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (priceWouldPay !== price) {
                        e.target.style.background = '#F0F4F5';
                      }
                    }}
                  >
                    {price}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 2: At what price would you absolutely not pay? */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 500, color: surveyErrors.priceWouldNotPay ? '#ef4444' : '#163C48', marginBottom: '12px' }}>
                At what price would you absolutely not pay?
                {surveyErrors.priceWouldNotPay && <span style={{ color: '#ef4444', marginLeft: '8px' }}>*</span>}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '8px' : '10px' }}>
                {priceOptionsNoLowest.map((price) => (
                  <button
                    key={price}
                    onClick={() => setPriceWouldNotPay(price)}
                    style={{
                      padding: isMobile ? '12px 8px' : '14px 12px',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      fontWeight: 500,
                      color: priceWouldNotPay === price ? '#fff' : '#163C48',
                      background: priceWouldNotPay === price ? safetyOrange : '#F0F4F5',
                      border: priceWouldNotPay === price ? 'none' : '1px solid #CFD8DC',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (priceWouldNotPay !== price) {
                        e.target.style.background = '#E0E6E8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (priceWouldNotPay !== price) {
                        e.target.style.background = '#F0F4F5';
                      }
                    }}
                  >
                    {price}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 3: Color Preference */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 500, color: surveyErrors.colorPreference ? '#ef4444' : '#163C48', marginBottom: '8px' }}>
                What color would you want?
                {surveyErrors.colorPreference && <span style={{ color: '#ef4444', marginLeft: '8px' }}>*</span>}
              </label>
              <p style={{ fontSize: '0.8rem', color: '#58737D', marginBottom: '12px', fontStyle: 'italic' }}>
                We will have limited availability for blue and pink. We will reserve either pink or blue if you choose one of those. No color limit on Clear.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {['Clear', 'Pink', 'Blue'].map((color) => (
                  <button
                    key={color}
                    onClick={() => setColorPreference(color)}
                    style={{
                      padding: isMobile ? '12px 8px' : '14px 12px',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      fontWeight: 500,
                      color: colorPreference === color ? '#fff' : '#163C48',
                      background: colorPreference === color ? safetyOrange : '#F0F4F5',
                      border: colorPreference === color ? 'none' : '1px solid #CFD8DC',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (colorPreference !== color) {
                        e.target.style.background = '#E0E6E8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (colorPreference !== color) {
                        e.target.style.background = '#F0F4F5';
                      }
                    }}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 5: Income */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 500, color: '#163C48', marginBottom: '8px' }}>
                Please choose your income range. <span style={{ color: '#58737D', fontWeight: 400 }}>(Optional)</span>
              </label>
              <p style={{ fontSize: '0.8rem', color: '#58737D', marginBottom: '12px', lineHeight: 1.5 }}>
                This is entirely optional, but helps us understand who we're building this for. Very helpful for us to use this data to understand our pricing better.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {incomeOptions.map((income) => (
                  <button
                    key={income}
                    onClick={() => setIncomeRange(income)}
                    style={{
                      padding: isMobile ? '12px 14px' : '14px 16px',
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      fontWeight: 500,
                      color: incomeRange === income ? '#fff' : '#163C48',
                      background: incomeRange === income ? safetyOrange : '#F0F4F5',
                      border: incomeRange === income ? 'none' : '1px solid #CFD8DC',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (incomeRange !== income) {
                        e.target.style.background = '#E0E6E8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (incomeRange !== income) {
                        e.target.style.background = '#F0F4F5';
                      }
                    }}
                  >
                    {income}
                  </button>
                ))}
              </div>
            </div>

            {/* Question 6: Role */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 500, color: surveyErrors.role ? '#ef4444' : '#163C48', marginBottom: '12px' }}>
                Who are you shopping for?
                {surveyErrors.role && <span style={{ color: '#ef4444', marginLeft: '8px' }}>*</span>}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {roleOptions.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRole(r)}
                    style={{
                      padding: isMobile ? '12px 8px' : '14px 12px',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      fontWeight: 500,
                      color: role === r ? '#fff' : '#163C48',
                      background: role === r ? safetyOrange : '#F0F4F5',
                      border: role === r ? 'none' : '1px solid #CFD8DC',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (role !== r) {
                        e.target.style.background = '#E0E6E8';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (role !== r) {
                        e.target.style.background = '#F0F4F5';
                      }
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Error message when validation fails */}
            {Object.keys(surveyErrors).length > 0 && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '16px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span style={{ color: '#ef4444', fontSize: '0.9rem', fontWeight: 500 }}>
                  Please answer all questions to continue.
                </span>
              </div>
            )}

            <button
              onClick={handleSurveySubmit}
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '16px',
                fontWeight: 600,
                color: '#ffffff',
                background: '#2D6E81',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 8px 20px rgba(45, 110, 129, 0.25)',
              }}
              onMouseEnter={(e) => { e.target.style.transform = 'scale(1.02)'; e.target.style.boxShadow = '0 10px 30px rgba(45, 110, 129, 0.3)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 8px 20px rgba(45, 110, 129, 0.25)'; }}
            >
              Complete Sign Up
            </button>
          </div>
        )}

        {/* SCREEN 3: THE CONFIRMATION */}
        {step === 3 && (
          <div style={{ textAlign: 'center' }}>
            {/* Animated Checkmark */}
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${safetyOrange}, #ff8c5a)`,
                margin: '0 auto 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'checkPop 0.5s ease',
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#163C48', marginBottom: '16px' }}>
              You're on the list.
            </h2>

            <p style={{ fontSize: '1.1rem', color: '#163C48', marginBottom: '8px' }}>
              You are <span style={{ color: safetyOrange, fontWeight: 700 }}>#{waitlistNumber}</span> in line.
            </p>

            <p style={{ fontSize: '0.95rem', color: '#58737D', lineHeight: 1.7, marginBottom: '32px' }}>
              Check your inbox. We've sent your confirmation number. When we are ready to ship, you'll receive a secure link to claim your 50% discount.
            </p>

            <button
              onClick={onClose}
              style={{
                padding: '14px 32px',
                fontSize: '15px',
                fontWeight: 500,
                color: '#163C48',
                background: '#F0F4F5',
                border: '1px solid #CFD8DC',
                borderRadius: '100px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.target.style.background = '#E0E6E8'; }}
              onMouseLeave={(e) => { e.target.style.background = '#F0F4F5'; }}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Inline keyframes for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes checkPop {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// SECTION 1: HERO - Decoupled Text + Media Blocks
// ============================================
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section style={{ background: '#F9F8F6' }}>
      {/* TOP BLOCK: Headline (Decoupled from image) */}
      <div
        style={{
          paddingTop: '100px',
          paddingBottom: '30px',
          paddingLeft: 'clamp(20px, 5vw, 60px)',
          paddingRight: 'clamp(20px, 5vw, 60px)',
          textAlign: 'center',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 1.5s ease, transform 1s ease',
        }}
      >
        <h1
          style={{
            fontSize: 'clamp(2rem, 6vw, 4rem)',
            fontWeight: 600,
            color: '#163C48',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            maxWidth: '900px',
            margin: '0 auto',
          }}
        >
          The World's First True Universal Hands-Free Baby Bottle Holder.
        </h1>
      </div>

      {/* MIDDLE BLOCK: Media (No text overlay) - Full width hero */}
      <div style={{ padding: '15px clamp(20px, 3vw, 40px)' }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          }}
        >
          <img
            src={alden46}
            alt="Baby Daddy Bottle Holder"
            style={{
              width: '100%',
              display: 'block',
            }}
          />
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 2: PHILOSOPHY - Transition Text Block
// ============================================
const PhilosophySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.5 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F9F8F6',
        padding: '15px clamp(20px, 3vw, 40px)',
      }}
    >
      <h2
        style={{
          fontSize: 'clamp(2rem, 5vw, 4rem)',
          fontWeight: 600,
          color: '#163C48',
          letterSpacing: '-0.02em',
          textAlign: 'center',
          maxWidth: '800px',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 1.2s ease, transform 1s ease',
        }}
      >
        Engineered for safety and reliability.
      </h2>
    </section>
  );
};

// ============================================
// PHOTO SECTION - Clean Headline + Subtitle
// ============================================
const PhotoSection = ({
  image,
  headline,
  subtitle,
  textPosition = 'bottom-left', // 'top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'
  onLearnMore, // Optional callback for Learn More button
  buttonLabel = 'Learn More', // Custom label for the button
  buttonPosition = null, // Optional separate position for button (e.g., 'bottom-right')
  preserveWidth = false, // Set to true for wide images that shouldn't be cropped horizontally
  compactText = false, // Set to true for smaller text on mobile
}) => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // MOBILE OVERRIDE: Heavier bottom gradient on mobile, original logic on desktop
  // Using Deep Navy (#163C48 = rgb(22, 60, 72)) instead of black
  const isTop = textPosition.startsWith('top');
  const gradientStyle = isMobile
    ? 'linear-gradient(to top, rgba(22, 60, 72, 0.95) 0%, rgba(22, 60, 72, 0.5) 40%, transparent 100%)'
    : (isTop
        ? 'linear-gradient(to bottom, rgba(22, 60, 72, 0.85) 0%, transparent 50%)'
        : 'linear-gradient(to top, rgba(22, 60, 72, 0.85) 0%, transparent 50%)');

  // Text positioning - respect top positions on mobile for compactText, otherwise bottom-left
  const getTextPositionStyle = () => {
    const base = { position: 'absolute' };

    // MOBILE: Respect top positions when compactText is used, otherwise force bottom-left
    if (isMobile) {
      if (compactText && textPosition.startsWith('top')) {
        // For compact text with top position, position at very top with tight spacing
        return { ...base, top: '12px', left: '16px', right: '16px', textAlign: textPosition.includes('center') ? 'center' : (textPosition.includes('right') ? 'right' : 'left') };
      }
      return { ...base, bottom: '24px', left: '20px', right: '20px', textAlign: 'left' };
    }

    // DESKTOP: Use the specified text position
    const spacing = 'clamp(16px, 4vw, 40px)';
    switch (textPosition) {
      case 'top-left':
        return { ...base, top: spacing, left: spacing, right: spacing, textAlign: 'left' };
      case 'top-center':
        return { ...base, top: spacing, left: spacing, right: spacing, textAlign: 'center' };
      case 'top-right':
        return { ...base, top: spacing, left: spacing, right: spacing, textAlign: 'right' };
      case 'bottom-left':
        return { ...base, bottom: spacing, left: spacing, right: spacing, textAlign: 'left' };
      case 'bottom-center':
        return { ...base, bottom: spacing, left: spacing, right: spacing, textAlign: 'center' };
      case 'bottom-right':
        return { ...base, bottom: spacing, left: spacing, right: spacing, textAlign: 'right' };
      default:
        return { ...base, bottom: spacing, left: spacing, right: spacing, textAlign: 'left' };
    }
  };

  const textPositionStyle = getTextPositionStyle();

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '900px', margin: '0 auto' }}>
        {/* Image Container */}
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(22, 60, 72, 0.2)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          <img
            src={image}
            alt={headline}
            style={{
              width: '100%',
              display: 'block',
              ...(preserveWidth ? {} : { minHeight: isMobile ? '65vh' : 'auto', objectFit: 'cover' }),
            }}
          />
          {/* Gradient Overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: gradientStyle,
              pointerEvents: 'none',
            }}
          />

          {/* Headline & Subtitle */}
          <div
            style={{
              position: 'absolute',
              ...textPositionStyle,
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
            }}
          >
            <h2
              style={{
                // MOBILE: Smaller font to not cover photos. DESKTOP: Original clamp
                fontSize: isMobile
                  ? (compactText ? '0.9rem' : '1.25rem')
                  : 'clamp(1.25rem, 4vw, 2.8rem)',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: subtitle ? (isMobile ? (compactText ? '4px' : '8px') : 'clamp(8px, 2vw, 16px)') : 0,
                textShadow: isMobile ? '0 4px 20px rgba(0,0,0,0.95)' : '0 4px 12px rgba(0,0,0,0.8)',
                lineHeight: 1.2,
              }}
            >
              {headline}
            </h2>
            {subtitle && (
              <p
                style={{
                  // MOBILE: Smaller font to not cover photos. DESKTOP: Original clamp
                  fontSize: isMobile
                    ? (compactText ? '0.7rem' : '0.85rem')
                    : 'clamp(0.85rem, 2vw, 1.25rem)',
                  color: 'rgba(255,255,255,0.9)',
                  maxWidth: isMobile ? (compactText ? '240px' : '280px') : '600px',
                  margin: isMobile ? undefined : (textPosition.includes('center') ? '0 auto' : undefined),
                  marginLeft: isMobile ? undefined : (textPosition.includes('right') ? 'auto' : undefined),
                  lineHeight: 1.3,
                  textShadow: isMobile ? '0 4px 20px rgba(0,0,0,0.95)' : '0 4px 12px rgba(0,0,0,0.8)',
                }}
              >
                {subtitle}
              </p>
            )}
            {onLearnMore && !buttonPosition && (
              <button
                onClick={onLearnMore}
                style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: '#fff',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '100px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: textPosition.includes('right') ? 'block' : 'inline-block',
                  marginLeft: textPosition.includes('right') ? 'auto' : undefined,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                }}
              >
                {buttonLabel}
              </button>
            )}
          </div>

          {/* Separate button when buttonPosition is specified */}
          {onLearnMore && buttonPosition && (
            <button
              onClick={onLearnMore}
              style={{
                position: 'absolute',
                bottom: buttonPosition.includes('bottom') ? 'clamp(12px, 3vw, 40px)' : undefined,
                top: buttonPosition.includes('top') ? 'clamp(12px, 3vw, 40px)' : undefined,
                right: buttonPosition.includes('right') ? 'clamp(12px, 3vw, 40px)' : undefined,
                left: buttonPosition.includes('left') ? 'clamp(12px, 3vw, 40px)' : undefined,
                padding: 'clamp(6px, 1.5vw, 10px) clamp(12px, 3vw, 20px)',
                fontSize: 'clamp(0.65rem, 2vw, 0.9rem)',
                fontWeight: 500,
                color: '#fff',
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '100px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                zIndex: 10,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              }}
            >
              {buttonLabel}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

// ============================================
// PREMIUM BUILD SECTION - Carousel with 2 slides
// ============================================
const PremiumBuildSection = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  const slides = [
    {
      image: alden37,
      headline: "Premium Build.",
      subtitle: "Made from BPA-Free PETG plastic. Impact resistant, durable, and dishwasher safe up to 180°F."
    },
    {
      image: capsAndCylinder,
      headline: "One Main Cylinder + 4 Lids.",
      subtitle: "Designed to accommodate nearly any baby bottle in the world globally."
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '900px', margin: '0 auto' }}>
        {/* Image Container */}
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(22, 60, 72, 0.2)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          {/* Images - stacked with opacity transition */}
          {slides.map((slide, index) => (
            <img
              key={index}
              src={slide.image}
              alt={slide.headline}
              style={{
                width: '100%',
                height: index === 0 ? 'auto' : '100%',
                minHeight: isMobile ? '65vh' : 'auto',
                objectFit: index === 1 ? 'contain' : 'cover',
                objectPosition: 'center center',
                display: 'block',
                position: index === 0 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                opacity: index === currentIndex ? 1 : 0,
                transition: 'opacity 0.5s ease',
                backgroundColor: index === 1 ? '#F9F8F6' : 'transparent',
              }}
            />
          ))}

          {/* Gradient Overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isMobile
                ? 'linear-gradient(to top, rgba(22, 60, 72, 0.95) 0%, rgba(22, 60, 72, 0.5) 40%, transparent 100%)'
                : 'linear-gradient(to bottom, rgba(22, 60, 72, 0.85) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          />

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            style={{
              position: 'absolute',
              left: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button
            onClick={goNext}
            style={{
              position: 'absolute',
              right: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* Content - Dynamic headline and subtitle */}
          <div
            style={{
              position: 'absolute',
              top: isMobile ? undefined : 'clamp(16px, 4vw, 40px)',
              bottom: isMobile ? '24px' : undefined,
              left: isMobile ? '20px' : undefined,
              right: isMobile ? '20px' : 'clamp(16px, 4vw, 40px)',
              textAlign: isMobile ? 'left' : 'right',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
            }}
          >
            <h2
              key={`headline-${currentIndex}`}
              style={{
                fontSize: isMobile ? '1.25rem' : 'clamp(1.25rem, 4vw, 2.8rem)',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: isMobile ? '8px' : 'clamp(8px, 2vw, 16px)',
                textShadow: isMobile ? '0 4px 20px rgba(0,0,0,0.95)' : '0 4px 12px rgba(0,0,0,0.8)',
                lineHeight: 1.2,
                transition: 'opacity 0.5s ease',
              }}
            >
              {slides[currentIndex].headline}
            </h2>
            <p
              key={`subtitle-${currentIndex}`}
              style={{
                fontSize: isMobile ? '0.85rem' : 'clamp(0.85rem, 2vw, 1.25rem)',
                color: 'rgba(255,255,255,0.9)',
                maxWidth: isMobile ? '280px' : '600px',
                marginLeft: isMobile ? undefined : 'auto',
                lineHeight: 1.3,
                textShadow: isMobile ? '0 4px 20px rgba(0,0,0,0.95)' : '0 4px 12px rgba(0,0,0,0.8)',
                transition: 'opacity 0.5s ease',
              }}
            >
              {slides[currentIndex].subtitle}
            </p>

            {/* Dot indicators */}
            <div style={{
              display: 'flex',
              gap: '6px',
              marginTop: 'clamp(10px, 1.5vw, 14px)',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
            }}>
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: index === currentIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease',
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// COLOR OPTIONS SECTION - Click through colors
// ============================================
const ColorOptionsSection = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  const colorOptions = [
    {
      image: alden18Clear,
      label: 'Clear with Grey Clamp',
    },
    {
      image: alden18Pink,
      label: 'Pink with Pink Clamp',
    },
    {
      image: alden18Blue,
      label: 'Blue with Blue Clamp',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % colorOptions.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + colorOptions.length) % colorOptions.length);
  };

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '900px', margin: '0 auto' }}>
        {/* Image Container */}
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(22, 60, 72, 0.2)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          {/* Images - stacked with opacity transition */}
          {colorOptions.map((option, index) => (
            <img
              key={index}
              src={option.image}
              alt={option.label}
              style={{
                width: '100%',
                minHeight: isMobile ? '65vh' : 'auto',
                objectFit: 'cover',
                objectPosition: 'center top',
                display: index === 0 ? 'block' : 'none',
                position: index === 0 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                opacity: index === currentIndex ? 1 : 0,
                transition: 'opacity 0.5s ease',
                ...(index !== 0 && { display: 'block', position: 'absolute' }),
              }}
            />
          ))}

          {/* Gradient Overlay - heavier on mobile */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isMobile
                ? 'linear-gradient(to top, rgba(22, 60, 72, 0.95) 0%, rgba(22, 60, 72, 0.5) 40%, transparent 100%)'
                : 'linear-gradient(to top, rgba(22, 60, 72, 0.85) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          />

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            style={{
              position: 'absolute',
              left: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button
            onClick={goNext}
            style={{
              position: 'absolute',
              right: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* Content */}
          <div
            style={{
              position: 'absolute',
              bottom: 'clamp(8px, 1.5vw, 12px)',
              left: 'clamp(12px, 2vw, 20px)',
              maxWidth: '320px',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
            }}
          >
            <h2
              style={{
                fontSize: 'clamp(1.4rem, 3.75vw, 2.25rem)',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: 'clamp(4px, 1vw, 8px)',
                textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                lineHeight: 1.2,
              }}
            >
              Available in three different color options.
            </h2>
            <p
              style={{
                fontSize: 'clamp(1.05rem, 2.25vw, 1.4rem)',
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.4,
                textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                transition: 'opacity 0.3s ease',
              }}
              key={currentIndex}
            >
              {colorOptions[currentIndex].label}
            </p>

            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: '6px', marginTop: 'clamp(6px, 1vw, 10px)' }}>
              {colorOptions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: index === currentIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease',
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// ANGLE FLEX SECTION - Adjustability Carousel
// ============================================
const AngleFlexSection = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  const angleOptions = [
    {
      image: alden03,
      headline: "Precision Angling.",
      subtitle: "Adjust to any angle you feel comfortable with. Feed upright, horizontal, or anywhere in between."
    },
    {
      image: alden01,
      headline: "",
      subtitle: ""
    },
    {
      image: alden14,
      headline: "Gravity Assist.",
      subtitle: "Perfect for ensuring steady flow without air bubbles. You control the tilt."
    },
    {
      image: alden16,
      headline: "Total Control.",
      subtitle: "The carbon-reinforced gooseneck holds firmly at every single degree of rotation."
    },
    {
      image: contentImage,
      headline: "Completely horizontal and as low as you need.",
      subtitle: ""
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % angleOptions.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + angleOptions.length) % angleOptions.length);
  };

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
        minHeight: isMobile ? '70vh' : '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '900px', margin: '0 auto' }}>
        {/* Image Container */}
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          {/* Images - stacked with opacity transition */}
          {angleOptions.map((option, index) => (
            <img
              key={index}
              src={option.image}
              alt={option.headline}
              style={{
                width: '100%',
                height: index === 0 ? 'auto' : '100%',
                minHeight: isMobile ? '65vh' : 'auto',
                objectFit: index === 1 ? 'contain' : 'cover',
                objectPosition: 'center center',
                display: 'block',
                position: index === 0 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                opacity: index === currentIndex ? 1 : 0,
                transition: 'opacity 0.5s ease',
                backgroundColor: index === 1 ? '#F9F8F6' : 'transparent',
              }}
            />
          ))}

          {/* Gradient Overlay - heavier on mobile */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isMobile
                ? 'linear-gradient(to top, rgba(22, 60, 72, 0.95) 0%, rgba(22, 60, 72, 0.5) 40%, transparent 100%)'
                : 'linear-gradient(to top, rgba(22, 60, 72, 0.85) 0%, transparent 50%)',
              pointerEvents: 'none',
            }}
          />

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            style={{
              position: 'absolute',
              left: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button
            onClick={goNext}
            style={{
              position: 'absolute',
              right: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* Content - Dynamic headline and subtitle */}
          <div
            style={{
              position: 'absolute',
              bottom: 'clamp(8px, 1.5vw, 12px)',
              left: 'clamp(12px, 2vw, 20px)',
              maxWidth: isMobile ? '85%' : '400px',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
            }}
          >
            <h2
              key={`headline-${currentIndex}`}
              style={{
                fontSize: 'clamp(1.4rem, 3.75vw, 2.25rem)',
                fontWeight: 600,
                color: '#ffffff',
                marginBottom: 'clamp(4px, 1vw, 8px)',
                textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                lineHeight: 1.2,
                opacity: 1,
                transition: 'opacity 0.5s ease',
              }}
            >
              {angleOptions[currentIndex].headline}
            </h2>
            <p
              key={`subtitle-${currentIndex}`}
              style={{
                fontSize: 'clamp(0.95rem, 2vw, 1.25rem)',
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.5,
                textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                transition: 'opacity 0.5s ease',
              }}
            >
              {angleOptions[currentIndex].subtitle}
            </p>

            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: '6px', marginTop: 'clamp(10px, 1.5vw, 14px)' }}>
              {angleOptions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: index === currentIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease',
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 6: SPLIT SCREEN GRID
// ============================================
const SplitGridSection = ({ leftImage, rightImage, leftHeadline, rightHeadline, leftBody, rightBody }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const CardStyle = {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    minHeight: '250px',
  };

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1050px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: 'clamp(15px, 3vw, 30px)',
          alignItems: 'stretch',
        }}
      >
        {/* Left Card - Text Top-Left */}
        <div
          style={{
            ...CardStyle,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          <img src={leftImage} alt={leftHeadline} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to bottom, rgba(22, 60, 72, 0.85) 0%, transparent 60%)' }} />
          <div style={{ position: 'absolute', top: 'clamp(16px, 4vw, 30px)', left: 'clamp(16px, 4vw, 30px)', right: 'clamp(16px, 4vw, 30px)' }}>
            <h3 style={{ fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontWeight: 600, color: '#fff', marginBottom: 'clamp(4px, 1vw, 8px)', textShadow: '0 4px 12px rgba(0,0,0,0.8)', lineHeight: 1.2 }}>{leftHeadline}</h3>
            <p style={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)', color: 'rgba(255,255,255,0.9)', lineHeight: 1.5, textShadow: '0 4px 12px rgba(0,0,0,0.8)' }}>{leftBody}</p>
          </div>
        </div>

        {/* Right Card - Text Top-Right */}
        <div
          style={{
            ...CardStyle,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease 0.15s, transform 1s ease 0.15s',
          }}
        >
          <img src={rightImage} alt={rightHeadline} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to bottom, rgba(22, 60, 72, 0.85) 0%, transparent 60%)' }} />
          <div style={{ position: 'absolute', top: 'clamp(16px, 4vw, 30px)', left: 'clamp(16px, 4vw, 30px)', right: 'clamp(16px, 4vw, 30px)', textAlign: 'right' }}>
            <h3 style={{ fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontWeight: 600, color: '#fff', marginBottom: 'clamp(4px, 1vw, 8px)', textShadow: '0 4px 12px rgba(0,0,0,0.8)', lineHeight: 1.2 }}>{rightHeadline}</h3>
            <p style={{ fontSize: 'clamp(0.85rem, 2vw, 1rem)', color: 'rgba(255,255,255,0.9)', lineHeight: 1.5, textShadow: '0 4px 12px rgba(0,0,0,0.8)' }}>{rightBody}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// ATTACH ANYWHERE CAROUSEL SECTION
// ============================================
const AttachAnywhereSection = () => {
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);

  const attachOptions = [
    {
      image: alden12,
      headline: "Attach Anywhere.",
      subtitle: "Cribs. Strollers. High Chairs. You name it.",
      isWarning: false,
    },
    {
      image: alden27,
      headline: "",
      subtitle: "",
      isWarning: true,
      warningText: "Not recommended for moving vehicles. Some parents use car seats at home for seated feeding - this is designed for that use only.",
    },
    {
      image: strollerImage,
      headline: "",
      subtitle: "",
      isWarning: false,
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % attachOptions.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + attachOptions.length) % attachOptions.length);
  };

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
        minHeight: isMobile ? '55vh' : '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '900px', margin: '0 auto' }}>
        {/* Image Container */}
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          {/* Images - stacked with opacity transition */}
          {attachOptions.map((option, index) => (
            <img
              key={index}
              src={option.image}
              alt={option.headline || 'Baby Daddy Holder'}
              style={{
                width: '100%',
                height: index === 0 ? 'auto' : '100%',
                minHeight: isMobile ? '50vh' : 'auto',
                objectFit: 'cover',
                objectPosition: 'center center',
                display: 'block',
                position: index === 0 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                opacity: index === currentIndex ? 1 : 0,
                transition: 'opacity 0.5s ease',
              }}
            />
          ))}

          {/* Gradient Overlay */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: attachOptions[currentIndex].headline
                ? (isMobile
                  ? 'linear-gradient(to top, rgba(22, 60, 72, 0.95) 0%, rgba(22, 60, 72, 0.5) 40%, transparent 100%)'
                  : 'linear-gradient(to top, rgba(22, 60, 72, 0.85) 0%, transparent 50%)')
                : 'linear-gradient(to top, rgba(22, 60, 72, 0.3) 0%, transparent 30%)',
              pointerEvents: 'none',
            }}
          />

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            style={{
              position: 'absolute',
              left: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button
            onClick={goNext}
            style={{
              position: 'absolute',
              right: 'clamp(6px, 1.5vw, 15px)',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'clamp(32px, 6vw, 44px)',
              height: 'clamp(32px, 6vw, 44px)',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* Warning Banner - for car seat slide */}
          {attachOptions[currentIndex].isWarning && (
            <div
              style={{
                position: 'absolute',
                top: isMobile ? '8px' : 'clamp(12px, 2vw, 20px)',
                left: isMobile ? '8px' : 'clamp(12px, 2vw, 20px)',
                right: isMobile ? '8px' : 'clamp(12px, 2vw, 20px)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: isMobile ? '8px' : '12px',
                padding: isMobile ? '10px 12px' : '18px 20px',
                background: 'rgba(255, 193, 7, 0.95)',
                border: '2px solid #FFC107',
                borderRadius: isMobile ? '8px' : '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                zIndex: 10,
              }}
            >
              <svg
                width={isMobile ? "18" : "28"}
                height={isMobile ? "18" : "28"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000"
                strokeWidth="2.5"
                style={{ flexShrink: 0, marginTop: '1px' }}
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p style={{
                color: '#000',
                fontSize: isMobile ? '0.7rem' : '0.95rem',
                fontWeight: 600,
                lineHeight: 1.4,
                margin: 0,
              }}>
                {attachOptions[currentIndex].warningText}
              </p>
            </div>
          )}

          {/* Content - Dynamic headline and subtitle */}
          <div
            style={{
              position: 'absolute',
              bottom: 'clamp(8px, 1.5vw, 12px)',
              left: 'clamp(12px, 2vw, 20px)',
              maxWidth: isMobile ? '85%' : '400px',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.3s, transform 1s ease 0.3s',
            }}
          >
            {attachOptions[currentIndex].headline && (
              <h2
                style={{
                  fontSize: 'clamp(1.4rem, 3.75vw, 2.25rem)',
                  fontWeight: 600,
                  color: '#ffffff',
                  marginBottom: 'clamp(4px, 1vw, 8px)',
                  textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                  lineHeight: 1.2,
                }}
              >
                {attachOptions[currentIndex].headline}
              </h2>
            )}
            {attachOptions[currentIndex].subtitle && (
              <p
                style={{
                  fontSize: 'clamp(0.95rem, 2vw, 1.25rem)',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.5,
                  textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                }}
              >
                {attachOptions[currentIndex].subtitle}
              </p>
            )}

            {/* Dot indicators */}
            <div style={{ display: 'flex', gap: '6px', marginTop: 'clamp(10px, 1.5vw, 14px)' }}>
              {attachOptions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: index === currentIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.3s ease',
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// VEHICLE WARNING MODAL
// ============================================
const VehicleWarningModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(22, 60, 72, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '500px',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 20px 60px rgba(22, 60, 72, 0.15)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '44px',
            height: '44px',
            background: '#F0F4F5',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Warning Icon */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'rgba(255, 107, 53, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
          }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>

        <h2 style={{ color: '#163C48', fontSize: '1.4rem', fontWeight: 600, marginBottom: '16px', textAlign: 'center', paddingRight: '20px' }}>
          Important Safety Note
        </h2>

        <p style={{ color: '#58737D', fontSize: '1rem', lineHeight: 1.7, textAlign: 'center' }}>
          We do not recommend using this in moving vehicles for car seats. Some parents however use a car seat in their home when letting the baby sit. This is just meant to be used for that example.
        </p>

        <button
          onClick={onClose}
          style={{
            display: 'block',
            width: '100%',
            marginTop: '24px',
            padding: '14px',
            background: '#2D6E81',
            color: '#fff',
            border: 'none',
            borderRadius: '100px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          I Understand
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// FAQ MODAL - Safety & FAQ Accordion
// ============================================
const FAQModal = ({ isOpen, onClose }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const faqData = [
    {
      category: "Safety & Mechanics",
      questions: [
        {
          q: "Isn't \"bottle propping\" dangerous?",
          a: "Yes, traditional bottle propping (using pillows or blankets) is dangerous because those items can slip, smother, or force a bottle into a baby's mouth without a way to escape.\n\nThe Baby Daddy Difference: Our device is not a prop; it is a suspension system. It holds the bottle in a fixed, rigid position. Because it does not rest on the baby, if the baby turns their head away, the bottle does not follow them. It stays put, allowing the baby to break the latch instantly."
        },
        {
          q: "What prevents the baby from choking or aspirating milk?",
          a: "The flow of milk is determined by the bottle, not the holder.\n\nOur Recommendation: We strongly advise using Anti-Colic or Suction-Activated bottles (like Dr. Brown's, Avent Natural, or Calma). These bottles only release milk when the baby actively sucks. If the baby stops feeding or falls asleep, the milk stops flowing. Never use this device with free-flowing \"gravity\" nipples that drip continuously."
        },
        {
          q: "Does the holder rest on the baby's chest or face?",
          a: "Never. The carbon-reinforced gooseneck is rated to hold up to 2 lbs horizontally without sagging. It hovers above the baby. This ensures zero weight is placed on the infant's chest, ensuring their breathing is never obstructed. The baby is only ever exposed to just the nipple of the bottle. That's it."
        }
      ]
    },
    {
      category: "Parenting Philosophy",
      questions: [
        {
          q: "Does this replace bonding time with my child?",
          a: "Absolutely not. Feeding is a sacred time. The Baby Daddy Holder is designed for the other times, when you have twins and only two hands, or when a parent with physical disabilities needs assistance. It is a \"Third Hand\" for the moments when life gets chaotic, ensuring your baby is fed on time, every time."
        },
        {
          q: "Is this just for \"lazy\" parents?",
          a: "We built this for busy parents, parents of multiples, and parents with limited mobility. Whether you are managing a toddler while feeding a newborn, recovering from a C-section, or simply need to eat your own dinner while the baby feeds next to you, this tool provides freedom without compromising the baby's feeding schedule."
        }
      ]
    },
    {
      category: "Certification & Trust",
      questions: [
        {
          q: "Is this product certified safe?",
          a: "We are currently in the pre-launch phase. Before a single unit is shipped to a customer, this product will undergo rigorous third-party testing to meet CPSC (Consumer Product Safety Commission) standards for infant products. We are also establishing a board of Pediatricians and Occupational Therapists to review and endorse our usage guidelines regarding feeding angles and latch mechanics."
        },
        {
          q: "What if I have a weirdly shaped bottle?",
          a: "If it's round and holds fluid, we hold it. Our kit comes with 4 interchangeable retention rings (2.00\", 2.375\", 2.475\", and 2.875\"). This covers the vast majority of global bottle brands, from skinny glass bottles to wide-neck silicone ones."
        }
      ]
    }
  ];

  // Flatten questions for index tracking
  let globalIndex = 0;
  const getGlobalIndex = () => globalIndex++;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(22, 60, 72, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#F9F8F6',
          borderRadius: '16px',
          padding: isMobile ? '30px 20px' : '40px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 20px 60px rgba(22, 60, 72, 0.15)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '44px',
            height: '44px',
            background: '#FFFFFF',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Header */}
        <h2 style={{ color: '#163C48', fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', paddingRight: '40px' }}>
          Safety & FAQ
        </h2>
        <p style={{ color: '#58737D', fontSize: '0.95rem', marginBottom: '28px' }}>
          Common questions about safety, usage, and certification.
        </p>

        {/* FAQ Categories */}
        {faqData.map((category, catIndex) => (
          <div key={catIndex} style={{ marginBottom: '24px' }}>
            <h3 style={{
              color: '#2D6E81',
              fontSize: '0.85rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px',
            }}>
              {category.category}
            </h3>

            {category.questions.map((item) => {
              const idx = getGlobalIndex();
              const isExpanded = openIndex === idx;

              return (
                <div
                  key={idx}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: '10px',
                    marginBottom: '8px',
                    border: '1px solid #CFD8DC',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => setOpenIndex(isExpanded ? null : idx)}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{
                      color: '#163C48',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      paddingRight: '16px',
                    }}>
                      {item.q}
                    </span>
                    <span style={{
                      color: '#2D6E81',
                      fontSize: '1.5rem',
                      fontWeight: 300,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}>
                      {isExpanded ? '−' : '+'}
                    </span>
                  </button>

                  {isExpanded && (
                    <div style={{
                      padding: '0 20px 20px',
                      borderTop: '1px solid #CFD8DC',
                    }}>
                      <p style={{
                        color: '#58737D',
                        fontSize: '0.9rem',
                        lineHeight: 1.7,
                        marginTop: '16px',
                        whiteSpace: 'pre-line',
                      }}>
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// SECTION 7B: DATA GRID FOR SPECS
// ============================================
const SpecsSection = ({ image, headline, specs, body }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          <img src={image} alt={headline} style={{ width: '100%', display: 'block' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to bottom, rgba(22, 60, 72, 0.9) 0%, rgba(22, 60, 72, 0.2) 60%)' }} />

          {/* Content - positioned at top to not cover bottles */}
          <div style={{ position: 'absolute', top: 'clamp(16px, 4vw, 30px)', left: 'clamp(10px, 3vw, 40px)', right: 'clamp(10px, 3vw, 40px)' }}>
            <h2 style={{ fontSize: 'clamp(0.9rem, 2.5vw, 2rem)', fontWeight: 600, color: '#fff', marginBottom: 'clamp(4px, 1vw, 16px)', textShadow: '0 2px 4px rgba(0,0,0,0.8)', lineHeight: 1.2 }}>
              {headline}
            </h2>

            {/* Data Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 'clamp(4px, 1vw, 12px)',
                marginBottom: 'clamp(4px, 1vw, 12px)',
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 1s ease 0.5s',
              }}
            >
              {specs.map((spec, i) => (
                <div
                  key={i}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    padding: 'clamp(4px, 1vw, 12px)',
                    borderRadius: 'clamp(4px, 1vw, 8px)',
                    textAlign: 'center',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <span style={{ fontSize: 'clamp(0.55rem, 1.5vw, 1rem)', fontWeight: 600, color: '#fff', fontFamily: 'monospace' }}>{spec}</span>
                </div>
              ))}
            </div>

            <p style={{ fontSize: 'clamp(0.65rem, 1.5vw, 0.9rem)', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>{body}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// SECTION 11: DISCLAIMER (WARNING STYLE)
// ============================================
const DisclaimerSection = ({ image, headline, body }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div style={{ width: '100%' }}>
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          <img src={image} alt={headline} style={{ width: '100%', display: 'block' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(22, 60, 72, 0.85) 0%, transparent 50%)' }} />

          {/* Warning Label Content */}
          <div
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '40px',
              right: '40px',
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 1s ease 0.3s',
            }}
          >
            {/* Warning Box */}
            <div
              style={{
                display: 'inline-block',
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '2px solid #facc15',
                borderRadius: '8px',
                padding: '24px 32px',
                maxWidth: '600px',
              }}
            >
              {/* Warning Icon + Headline */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <h2
                  style={{
                    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                    fontWeight: 600,
                    color: '#facc15',
                    textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                  }}
                >
                  {headline}
                </h2>
              </div>
              <p
                style={{
                  fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.6,
                  textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                }}
              >
                {body}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOUNDER MODAL - A Note from the Founder
// ============================================
const FounderModal = ({ isOpen, onClose }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const paragraphs = [
    "Hi! My name is Alden. I am the founder and inventor of the Baby Daddy Bottle Holder.",
    "I have been an entrepreneur nearly all my life. During my early 20s, I ran off to Italy and invented Edible Coffee Cups. You can look me up on YouTube if you want to learn more about me and see some of the things I worked on (including the cool edible coffee cups). After that, I ran off to Stanford University where I founded an app called GEO.",
    "When I finally ran out of money, I went back to college. I graduated from The Ohio State University in 2016. After graduating college, I ran back to California, this time to Southern California. I got my first job as a recruiter at a company called Jobspring Partners.",
    "After leaving Jobspring Partners, I went on to build my own recruiting agency, where I had lots of success placing engineers for world-class teams all over the country. During my time as a recruiter, I built other apps, like Machine Sourcer (which automated sourcing activity on LinkedIn) and a geolocation networking app called Drop. After working on my own as a recruiter, I decided to go internal when COVID hit. I worked for companies like Lime, Amazon, and a popular B2B data analytics company called 6sense.",
    "I am a former foster child. I aged out of foster care in 2010 after graduating high school.",
    "I am also a father to a beautiful little girl named Chazmin. She shares my middle name. This product was invented because of her. This product has taken nearly two years of persistence and overcoming things that at times seemed impossible. Sometimes, they were impossible. This bottle holder was engineered with absolute precision and rigorous testing, meant to balance ease of use with reliability when it comes to holding weight.",
    "It is production-ready. I just need your help getting enough validation and signals as I prepare for this next step with our manufacturing partners.",
    "My story is one of hope and promise. This product is made from love and a relentless conviction to bring something to market that most would never have thought could take off. I believe this product will be a lifesaver for millions of parents, especially single parents, children with physical disabilities, and many more.",
    "I came up with the name Baby Daddy because I am a dad, and I try to do the best I can as a Baby Daddy. This bottle holder will do the best it can for you and your baby."
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: 'rgba(22, 60, 72, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
      onClick={onClose}
    >
      {/* Content Box */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: isMobile ? '30px 20px' : '50px',
          maxWidth: '720px',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #CFD8DC',
          boxShadow: '0 20px 60px rgba(22, 60, 72, 0.15)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '44px',
            height: '44px',
            background: '#F0F4F5',
            border: '1px solid #CFD8DC',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#163C48" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Photo - Larger, centered */}
        <div
          style={{
            width: isMobile ? '180px' : '220px',
            height: isMobile ? '180px' : '220px',
            margin: '20px auto 30px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid #CFD8DC',
          }}
        >
          <img
            src={founderPhoto}
            alt="Alden Morris - Founder"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </div>

        {/* Body Text */}
        <div>
          {paragraphs.map((text, index) => (
            <p
              key={index}
              style={{
                color: '#58737D',
                fontSize: isMobile ? '0.95rem' : '1.05rem',
                lineHeight: 1.8,
                marginBottom: '20px',
                textAlign: 'left',
              }}
            >
              {text}
            </p>
          ))}
        </div>

        {/* Signature */}
        <p
          style={{
            color: '#163C48',
            fontSize: isMobile ? '1.1rem' : '1.25rem',
            fontWeight: 600,
            fontStyle: 'italic',
            textAlign: 'right',
            marginTop: '30px',
          }}
        >
          - Alden Morris
        </p>

        {/* Contact Info */}
        <p
          style={{
            color: '#58737D',
            fontSize: isMobile ? '0.85rem' : '0.95rem',
            textAlign: 'center',
            marginTop: '40px',
            paddingTop: '25px',
            borderTop: '1px solid #CFD8DC',
            lineHeight: 1.7,
          }}
        >
          Have feedback or just want to connect?<br />
          Send me an email at{' '}
          <a
            href="mailto:alden@babydaddyholder.com"
            style={{
              color: '#2D6E81',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            alden@babydaddyholder.com
          </a>
          <br />
          <span style={{ fontSize: '0.85em', color: '#58737D' }}>
            I will try to respond to as many people as I can.
          </span>
        </p>
      </div>

      {/* Inline keyframes for fade animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ============================================
// SECTION 12: CLOSE WITH CTA
// ============================================
const CloseSection = ({ image, headline, onOpenWaitlist, onOpenFounder, onOpenPromise }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        padding: '15px clamp(20px, 3vw, 40px)',
        background: '#F9F8F6',
      }}
    >
      <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease, transform 1s ease',
          }}
        >
          <img src={image} alt={headline} style={{ width: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(22, 60, 72, 0.85) 0%, rgba(22, 60, 72, 0.2) 60%)' }} />

          {/* Content */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              width: '90%',
            }}
          >
            <h2
              style={{
                fontSize: 'clamp(1.5rem, 5vw, 3.5rem)',
                fontWeight: 600,
                color: '#fff',
                marginBottom: 'clamp(20px, 4vw, 40px)',
                textShadow: '0 4px 30px rgba(0,0,0,0.5)',
              }}
            >
              {headline}
            </h2>

            <button
              onClick={onOpenWaitlist}
              style={{
                padding: 'clamp(12px, 2.5vw, 18px) clamp(28px, 6vw, 48px)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                fontWeight: 600,
                color: '#000',
                background: '#fff',
                border: 'none',
                borderRadius: '100px',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              }}
              onMouseEnter={(e) => { e.target.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
            >
              Reserve for Free
            </button>

            {/* Founder Button */}
            {onOpenFounder && (
              <button
                onClick={onOpenFounder}
                style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: '#fff',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '100px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'block',
                  margin: '16px auto 0',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                }}
              >
                A Note from The Founder
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// NAVIGATION
// ============================================
const Navigation = ({ onOpenWaitlist, onOpenPromise, onOpenFAQ }) => {
  const [scrolled, setScrolled] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: isMobile ? '8px 10px' : '16px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: scrolled ? 'rgba(249, 248, 246, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Left: Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: isMobile ? '0 0 auto' : '1' }}>
        <div
          style={{
            width: isMobile ? '32px' : '40px',
            height: isMobile ? '32px' : '40px',
            borderRadius: '50%',
            background: '#ffffff',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 10px rgba(22, 60, 72, 0.1)',
            flexShrink: 0,
          }}
        >
          <img src={logo} alt="Baby Daddy" style={{ width: '95%', height: '95%', objectFit: 'contain' }} />
        </div>
        {!isMobile && <span style={{ fontSize: '15px', fontWeight: 600, color: '#163C48' }}>Baby Daddy</span>}
      </div>

      {/* Center: FAQ & Safety Promise Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '12px', flex: isMobile ? '1' : 'none', justifyContent: 'center' }}>
        {/* Safety & FAQ Button */}
        <button
          onClick={onOpenFAQ}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '3px' : '6px',
            padding: isMobile ? '6px 8px' : '10px 20px',
            fontSize: isMobile ? '10px' : '14px',
            fontWeight: 600,
            color: '#ffffff',
            background: '#2D6E81',
            border: 'none',
            borderRadius: '100px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            boxShadow: isMobile ? 'none' : '0 8px 20px rgba(45, 110, 129, 0.25)',
          }}
        >
          <svg width={isMobile ? "10" : "16"} height={isMobile ? "10" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          {isMobile ? 'FAQ' : 'Safety & FAQ'}
        </button>

        {/* Safety Promise Button */}
        <button
          onClick={onOpenPromise}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '3px' : '6px',
            padding: isMobile ? '6px 8px' : '10px 20px',
            fontSize: isMobile ? '10px' : '14px',
            fontWeight: 600,
            color: '#ffffff',
            background: '#2D6E81',
            border: 'none',
            borderRadius: '100px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
            boxShadow: isMobile ? 'none' : '0 8px 20px rgba(45, 110, 129, 0.25)',
          }}
        >
          <svg width={isMobile ? "10" : "16"} height={isMobile ? "10" : "16"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          {isMobile ? 'Safety Promise' : 'Our Safety Promise'}
        </button>
      </div>

      {/* Right: Reserve for Free */}
      <div style={{ flex: isMobile ? '0 0 auto' : '1', display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onOpenWaitlist}
          style={{
            padding: isMobile ? '6px 10px' : '10px 22px',
            fontSize: isMobile ? '10px' : '14px',
            fontWeight: 600,
            color: '#ffffff',
            background: '#2D6E81',
            border: 'none',
            borderRadius: '100px',
            cursor: 'pointer',
            boxShadow: isMobile ? 'none' : '0 8px 20px rgba(45, 110, 129, 0.25)',
            whiteSpace: 'nowrap',
          }}
        >
          Reserve for Free
        </button>
      </div>
    </nav>
  );
};

// ============================================
// FOOTER
// ============================================
const Footer = ({ onOpenWaitlist }) => {
  return (
    <footer id="footer" style={{ padding: '120px 40px 80px', textAlign: 'center', background: '#163C48' }}>
      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#ffffff', margin: '0 auto 30px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)' }}>
        <img src={logo} alt="Baby Daddy" style={{ width: '95%', height: '95%', objectFit: 'contain' }} />
      </div>
      <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 600, color: '#ffffff', marginBottom: '16px' }}>Engineering For Parents.</h2>
      <p style={{ fontSize: '1rem', color: '#E0E6E8', marginBottom: '40px', maxWidth: '500px', margin: '0 auto 40px' }}>Be the first to know when the Baby Daddy Bottle Holder launches in summer of '26.</p>
      <button
        onClick={onOpenWaitlist}
        style={{
          padding: '18px 48px',
          fontSize: '1.1rem',
          fontWeight: 600,
          color: '#ffffff',
          background: '#2D6E81',
          border: 'none',
          borderRadius: '100px',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          boxShadow: '0 8px 20px rgba(45, 110, 129, 0.25)',
        }}
        onMouseEnter={(e) => { e.target.style.transform = 'scale(1.05)'; }}
        onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
      >
        Reserve for Free
      </button>
      <p style={{ marginTop: '60px', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>© {new Date().getFullYear()} Baby Daddy. All rights reserved.</p>
    </footer>
  );
};

// ============================================
// MAIN APP
// ============================================
function App() {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [isSafetyModalOpen, setIsSafetyModalOpen] = useState(false);
  const [isFounderModalOpen, setIsFounderModalOpen] = useState(false);
  const [isPromiseModalOpen, setIsPromiseModalOpen] = useState(false);
  const [isVehicleWarningOpen, setIsVehicleWarningOpen] = useState(false);
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  const openWaitlistModal = () => setIsWaitlistModalOpen(true);

  return (
    <div style={{ background: '#F9F8F6', minHeight: '100vh' }}>
      <Navigation onOpenWaitlist={openWaitlistModal} onOpenPromise={() => setIsPromiseModalOpen(true)} onOpenFAQ={() => setIsFAQModalOpen(true)} />

      {/* Modals */}
      <InfoModal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />
      <WaitlistModal isOpen={isWaitlistModalOpen} onClose={() => setIsWaitlistModalOpen(false)} />
      <SafetyModal isOpen={isSafetyModalOpen} onClose={() => setIsSafetyModalOpen(false)} />
      <FounderModal isOpen={isFounderModalOpen} onClose={() => setIsFounderModalOpen(false)} />
      <PromiseModal isOpen={isPromiseModalOpen} onClose={() => setIsPromiseModalOpen(false)} />
      <VehicleWarningModal isOpen={isVehicleWarningOpen} onClose={() => setIsVehicleWarningOpen(false)} />
      <FAQModal isOpen={isFAQModalOpen} onClose={() => setIsFAQModalOpen(false)} />

      {/* SECTION 1: HERO */}
      <HeroSection />

      {/* SECTION 2: PHILOSOPHY */}
      <PhilosophySection />

      {/* SECTION 3: MATERIALS */}
      <PremiumBuildSection />

      {/* SECTION 4: THE MECHANISM */}
      <PhotoSection
        image={puck}
        headline="Engineered for precision and ease."
        subtitle="Turn to tighten. Outer threads lock into the main cylinder for instant securement."
        textPosition="top-left"
      />

      {/* SECTION 5: THE ANCHOR */}
      <PhotoSection
        image={clamp}
        headline="Industrial Strength."
        subtitle="Lightweight but strong. Clamps onto surfaces up to 4 inches wide: wood, metal, or upholstery. Comes with anti-slip adhesive tape to double down on stability."
        textPosition="bottom-left"
        compactText={true}
      />

      {/* SECTION 6: ATTACH ANYWHERE CAROUSEL */}
      <AttachAnywhereSection />

      {/* ANGLE FLEX SECTION: Adjustability Carousel */}
      <AngleFlexSection />

      {/* SECTION 7A: UNIVERSAL COMPATIBILITY */}
      <PhotoSection
        image={alden57}
        headline="Fits every baby bottle up to 9oz in the world."
        subtitle="Glass. Plastic. Sippy Cups. Universal compatibility."
        textPosition="top-center"
        preserveWidth={true}
        compactText={true}
      />

      {/* SECTION 7B: SPECS GRID */}
      <SpecsSection
        image={alden58}
        headline="4 Lids. 4 Diameters."
        specs={['2.00"', '2.475"', '2.875"', '2.375"']}
        body="Includes 4 custom lids to accommodate nearly every global bottle brand."
      />

      {/* COLOR OPTIONS */}
      <ColorOptionsSection />

      {/* SECTION 9: STRENGTH */}
      <PhotoSection
        image={alden08}
        headline="Can hold up to 2 lbs horizontally."
        subtitle="Extremely easy to twist, bend, and adjust without sagging."
        textPosition="top-center"
      />

      {/* SECTION 10: ERGONOMICS & SAFETY */}
      <PhotoSection
        image={alden44}
        headline="No weight on the baby's face."
        subtitle="The holder does not rest on the baby. It hovers."
        textPosition="bottom-right"
        onLearnMore={() => setIsInfoModalOpen(true)}
        buttonLabel="Bottle Safety & Guidelines"
      />

      {/* SECTION 11: DISCLAIMER */}
      <PhotoSection
        image={alden45}
        headline="Peace of Mind."
        subtitle="Not meant to replace parent bonding or holding your baby yourself to feed, just meant to be an extra hand when you need it."
        textPosition="top-left"
        onLearnMore={() => setIsSafetyModalOpen(true)}
        buttonLabel="Age Guidelines & Safety"
      />

      {/* SECTION 12: THE CLOSE */}
      <CloseSection
        image={alden47}
        headline="Engineered with Care."
        onOpenWaitlist={openWaitlistModal}
        onOpenFounder={() => setIsFounderModalOpen(true)}
        onOpenPromise={() => setIsPromiseModalOpen(true)}
      />

      <Footer onOpenWaitlist={openWaitlistModal} />
    </div>
  );
}

export default App;
